/**
 * How a value is shown in a READ-ONLY grid cell.
 *
 * ⚠ READ-ONLY ONLY, and that restriction is the whole safety of it. An editable
 * cell shows exactly what Acumatica sent, because whatever is displayed is what
 * gets typed over and written back — reformatting one would mean a customer who
 * touches a field sends our rendering of their data rather than their data.
 * Read-only cells have no such path: nothing can be written from them.
 *
 * Which is why timestamps are the case worth handling. LastModifiedDateTime is
 * read-only and arrives as "2026-08-19T00:17:00.907+00:00", which in a column
 * narrow enough to fit on screen truncates to "2026-08-19T00:17:00.907+00:..."
 * — unreadable, and in a cleanup tool "when did this last change" is a column
 * people actually use to judge what is stale.
 */

/**
 * A complete ISO 8601 instant, and nothing looser.
 *
 * ⚠ Deliberately strict. A date-only string, a version number, a part code
 * shaped like a date — none of those are instants, and reformatting a value we
 * have merely guessed at would corrupt the customer's view of their own data.
 * Requires the date, the T, the time, and an explicit zone.
 */
const ISO_INSTANT = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?(Z|[+-]\d{2}:\d{2})$/;

export function isIsoInstant(value: string): boolean {
  return ISO_INSTANT.test(value) && !Number.isNaN(Date.parse(value));
}

/**
 * Shown in the viewer's own timezone, because the question being asked is
 * "how recently did someone touch this", and that is a local question.
 */
export function formatReadOnlyCell(text: string): string {
  if (!isIsoInstant(text)) return text;
  return new Date(text).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/** The untouched value, kept as a tooltip so nothing is actually hidden. */
export function readOnlyCellTitle(text: string): string | undefined {
  return isIsoInstant(text) ? text : undefined;
}

/**
 * The canonical text for a cell: what Acumatica sent, as a string.
 *
 * ⚠ NO PRETTIFYING, AND BOOLEANS ARE THE REASON THIS IS WRITTEN DOWN. The grid
 * used to render a boolean as `Yes`/`No`, and that string went everywhere the
 * value went: into the editable input the customer types over, into the edits
 * map, into the diff's before-value, and from there into the job payload.
 *
 * The consequences ran the whole way through the write path. `applyRecord`
 * compares the stored before-value against what Acumatica actually holds, so
 * `Yes` against `true` never matches — every boolean edit would have been
 * reported as *"changed in Acumatica since this edit was made"*, telling the
 * customer a third party had touched their record when nobody had. And had one
 * landed, it would have written the literal string `No` into a boolean field.
 *
 * Latent so far only because no boolean column is in the shipped set — but
 * custom Attributes are customer-defined, and a checkbox attribute is an
 * ordinary thing for someone to have.
 *
 * ⚠ The fix is to display the canonical value, NOT to invent a second encoding
 * that maps `Yes` back to `true` on the way out. The server already owns the
 * wire shape — `toWireValue` special-cases `note`, nested paths carry their
 * expand path — and a display-to-wire translation in the browser would be a
 * second place for that to be decided, disagreeing quietly.
 *
 * This is the rule the header of this file already stated: an editable cell
 * shows exactly what Acumatica sent, because whatever is displayed is what gets
 * typed over and written back. `formatCell` simply did not follow it.
 */
export function toCellText(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'object') return JSON.stringify(value);
  // Booleans included: String(true) is 'true', which is what came off the wire.
  return String(value);
}
